// /simulations/:slug landing - a "Your work" dashboard with a welcome from the
// architect. Board data comes from the API.
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowRight } from "react-icons/fi";
import { getBoard } from "../../features/simulation/simulationSlice";
import { useSimulation } from "./SimulationContext";
import { Avatar, Label, TypeIcon, StatusPill } from "./mithraUi";

function Stat({ num, lbl }) {
  return (
    <div className="mi-stat">
      <div className="num">{num}</div>
      <div className="lbl">{lbl}</div>
    </div>
  );
}

function MithraDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug, company, peopleByKey } = useSimulation();
  const { board } = useSelector((s) => s.simulation);

  useEffect(() => {
    dispatch(getBoard(slug));
  }, [dispatch, slug]);

  const cards = (board && board.cards) || [];
  const base = `/simulations/${slug}`;
  const architect = peopleByKey.Saman || Object.values(peopleByKey).find((p) => !p.isYou);

  const done = cards.filter((c) => c.status === "done");
  const inProgress = cards.filter((c) => c.status === "in_progress");
  const todo = cards.filter((c) => c.status === "todo");
  const points = cards.reduce((s, c) => s + (c.storyPoints || 0), 0);
  const donePoints = done.reduce((s, c) => s + (c.storyPoints || 0), 0);

  const upNext = [...inProgress, ...todo].slice(0, 5);
  const open = (card) => navigate(`${base}/ticket/${card.key}`);

  return (
    <div>
      <div className="mi-hero">
        <div style={{ display: "flex", gap: 14 }}>
          {architect && <Avatar person={architect} size="lg" />}
          <div>
            <h1>Welcome to the team 👋</h1>
            <p style={{ color: "var(--mi-text-subtle)", maxWidth: 720 }}>
              I've spun up the <strong>{company.projectName}</strong> project and queued your
              onboarding sprint. We've partnered with <strong>{company.partner}</strong> for order
              logistics - your job is to build the integration between our Salesforce org and their
              API, in both directions. Work the board top-down; each ticket builds on the last.
              {cards[0] ? (
                <>
                  {" "}
                  Start with <strong>{cards[0].key}</strong>.
                </>
              ) : null}
            </p>
            {architect && (
              <p style={{ marginTop: 6, fontSize: 12.5, color: "var(--mi-text-faint)" }}>
                - {architect.name}, {architect.role} · {architect.team}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mi-stat-row">
        <Stat num={cards.length} lbl="Assigned to you" />
        <Stat num={inProgress.length} lbl="In progress" />
        <Stat num={`${done.length}/${cards.length}`} lbl="Completed" />
        <Stat num={`${donePoints}/${points}`} lbl="Story points" />
      </div>

      <div className="mi-section-label" style={{ marginTop: 0 }}>
        Up next
      </div>
      <div className="mi-panel">
        {upNext.map((card) => (
          <div
            key={card.key}
            className="mi-details"
            style={{ cursor: "pointer" }}
            onClick={() => open(card)}
          >
            <div className="row" style={{ gridTemplateColumns: "auto 1fr auto" }}>
              <div className="v">
                <TypeIcon type={card.type} />
                <span className="mi-key">{card.key}</span>
              </div>
              <div className="v" style={{ fontWeight: 500 }}>
                {card.title}
                <span style={{ display: "inline-flex", gap: 4, marginLeft: 8 }}>
                  {card.labels.map((l) => (
                    <Label key={l} text={l} />
                  ))}
                </span>
              </div>
              <div className="v">
                <StatusPill status={card.status} />
              </div>
            </div>
          </div>
        ))}
        {upNext.length === 0 && cards.length > 0 && (
          <div className="mi-empty">Nothing left - sprint complete 🎉</div>
        )}
        {cards.length === 0 && <div className="mi-empty">Loading your work…</div>}
      </div>

      <div style={{ marginTop: 18 }}>
        <Link
          to={`${base}/board`}
          className="mi-btn primary"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          Go to the {company.projectKey} board <FiArrowRight />
        </Link>
      </div>
    </div>
  );
}

export default MithraDashboard;
